"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { MapPin, Phone, Mail, Globe } from "lucide-react";
export default function ContactInformation() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const regions = [{
    id: 1,
    name: "Middle East",
    offices: [{
      city: "Riyadh, KSA",
      address: "Prince Sultan St, Al Olaya District",
      phone: "+966 11 234 5678",
      email: "ksa@globalwholesaletrade.com",
      coordinates: {
        x: "62%",
        y: "42%"
      }
    }, {
      city: "Dubai, UAE",
      address: "Sheikh Zayed Road, DIFC",
      phone: "+971 4 123 4567",
      email: "uae@globalwholesaletrade.com",
      coordinates: {
        x: "64%",
        y: "45%"
      }
    }]
  }, {
    id: 2,
    name: "Africa",
    offices: [{
      city: "Lagos, Nigeria",
      address: "Victoria Island, Ahmadu Bello Way",
      phone: "+234 1 345 6789",
      email: "nigeria@globalwholesaletrade.com",
      coordinates: {
        x: "52%",
        y: "52%"
      }
    }]
  }, {
    id: 3,
    name: "Asia",
    offices: [{
      city: "Mumbai, India",
      address: "Nariman Point, Marine Drive",
      phone: "+91 22 1234 5678",
      email: "india@globalwholesaletrade.com",
      coordinates: {
        x: "70%",
        y: "48%"
      }
    }]
  }, {
    id: 4,
    name: "Europe",
    offices: [{
      city: "Frankfurt, Germany",
      address: "Mainzer Landstrasse, Financial District",
      phone: "+49 69 1234 5678",
      email: "germany@globalwholesaletrade.com",
      coordinates: {
        x: "50%",
        y: "28%"
      }
    }]
  }];
  return <section id="contact-information" ref={sectionRef} className="relative py-20 bg-background overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid lg:grid-cols-[3fr_2fr] gap-12 lg:gap-16 items-start">
          {}
          <div className="space-y-8">
            <div className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
              <h2 className="text-4xl font-display font-bold text-foreground mb-3">
                Our Global Offices
              </h2>
              <p className="text-muted-foreground text-lg">
                Connect with our regional teams across the Middle East, Africa,
                Asia, and Europe.
              </p>
            </div>

            <div className="space-y-6">
              {regions.map((region, regionIndex) => <div key={region.id} data-index={regionIndex} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
              transitionDelay: `${(regionIndex + 1) * 100}ms`
            }}>
                  <h3 className="text-xl font-display font-bold text-primary mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-accent" />
                    {region.name}
                  </h3>

                  <div className="space-y-4">
                    {region.offices.map((office, officeIndex) => <Card key={officeIndex} data-index={officeIndex} className="group cursor-pointer hover-lift border-border/50 hover:border-accent/30">
                        <CardContent className="p-5">
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-accent mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-[var(--duration-fast)]" />
                            <div className="space-y-2 flex-1">
                              <h4 className="font-display font-semibold text-foreground text-lg">
                                {office.city}
                              </h4>
                              <p className="text-muted-foreground text-sm">
                                {office.address}
                              </p>
                              <div className="flex flex-col gap-1.5 pt-2">
                                <Link to={`tel:${office.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors duration-[var(--duration-fast)]">
                                  <Phone className="w-4 h-4" />
                                  {office.phone}
                                </Link>
                                <Link to={`mailto:${office.email}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors duration-[var(--duration-fast)]">
                                  <Mail className="w-4 h-4" />
                                  {office.email}
                                </Link>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>)}
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div className={`relative transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]"}`} style={{
          transitionDelay: "200ms"
        }}>
            <div className="relative w-full aspect-[4/5] rounded-sm overflow-hidden bg-gradient-to-br from-primary via-primary-dark to-secondary shadow-strong">
              {}
              <div className="absolute inset-0 opacity-20">
                <svg className="w-full h-full" viewBox="0 0 400 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="400" height="500" fill="url(#grid)" />
                </svg>
              </div>

              {}
              <div className="absolute inset-0 opacity-30">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542426/img/world_map_continents_outline.png" alt="" className="w-full h-full object-cover" />
              </div>

              {}
              {regions.flatMap(region => region.offices.map((office, idx) => <div key={`${region.id}-${idx}`} data-index={idx} className="absolute group/node" style={{
              left: office.coordinates.x,
              top: office.coordinates.y,
              transform: "translate(-50%, -50%)"
            }}>
                    {}
                    <div className="absolute inset-0 w-8 h-8 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
                      <div className="absolute inset-0 rounded-full bg-accent/40 animate-ping" />
                    </div>

                    {}
                    <div className="relative w-4 h-4 rounded-full bg-accent shadow-[0_0_20px_rgba(212,167,44,0.8)] group-hover/node:scale-125 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)]" />

                    {}
                    <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover/node:opacity-100 pointer-events-none transition-opacity duration-[var(--duration-medium)] whitespace-nowrap">
                      <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-sm shadow-medium">
                        <p className="text-xs font-semibold text-primary">
                          {office.city}
                        </p>
                      </div>
                    </div>
                  </div>))}

              {}
              <div className="absolute inset-0 bg-gradient-to-t from-accent/10 via-transparent to-transparent pointer-events-none" />
            </div>

            {}
            <p className="text-center text-sm text-muted-foreground mt-4 font-display">
              Trade hubs across 4 continents
            </p>
          </div>
        </div>
      </div>
    </section>;
}